import React, { useState } from 'react';
import Header from './Header';
import authService from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';
import { useTextSize } from '../contexts/TextSizeContext';

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!email || !password) {
        throw new Error(t('auth.emailPasswordRequired'));
      }

      if (password.length < 6) {
        throw new Error(t('auth.passwordMinLength'));
      }

      const result = await authService.login(email, password, rememberMe);
      
      if (result.success) {
        onLogin(result.user);
      } else {
        throw new Error(result.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || t('auth.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen font-sans flex flex-col">
      <Header/>

      {/* Main Content - Centered */}
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 w-full">
        <div className="text-center mb-8">
          <h2 className={`${currentTextSize.headerText} font-bold text-gray-900 mb-2`}>{t('app.welcome')}</h2>
          <p className={`${currentTextSize.bodyText} text-gray-600`}>{t('app.signInSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className={`${currentTextSize.bodyText} text-red-600`}>{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="email" className={`block ${currentTextSize.labelText} font-medium text-gray-700 mb-1`}>{t('auth.email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              className={`w-full px-3 py-2 ${currentTextSize.inputText} border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="password" className={`block ${currentTextSize.labelText} font-medium text-gray-700 mb-1`}>{t('auth.password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordPlaceholder')}
              className={`w-full px-3 py-2 ${currentTextSize.inputText} border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
              required
              disabled={isLoading}
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={isLoading}
            />
            <label htmlFor="rememberMe" className={`ml-2 block ${currentTextSize.labelText} text-gray-700`}>
              {t('auth.rememberMe')}
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full bg-blue-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${currentTextSize.buttonText}`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('app.signingIn')}
              </span>
            ) : (
              t('app.signIn')
            )}
          </button>

          <p className={`text-center ${currentTextSize.buttonText} text-gray-500 mt-2`}>{t('app.loginCredentials')}</p>
        </form>
        </div>
      </div>
    </div>
  );
}